import { usePrivy } from "@privy-io/react-auth";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import type { EmblaEventType } from "embla-carousel";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { generateTextShadow } from "@/lib/utils";
import { useMusic } from "@/lib/hooks/useMusic";
import { useClaimWelcomePack, useStats } from "@/lib/hooks/useApi";
import { DefaultButton } from "./DefaultButton";
import AnimatedWaiting from "./AnimatedWaiting";
import { useAtomValue, useSetAtom } from "jotai";
import {
  homeUiReadyAtom,
  welcomePackDialogOpenAtom,
} from "@/lib/homeUiAtoms";
import { preload } from "react-dom";
import { RotateCcw } from "lucide-react";

type WelcomePhase = "intro" | "video" | "cards";

// const VIDEO_SRC = "https://www.addicted.fun/sample-open-pack.mp4";
const VIDEO_SRC = "https://cdn.navalabs.io//welcome-pack-2_bLx2T3.mp4";

const TWEEN_FACTOR_BASE = 0.52;

const numberWithinRange = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const bonusHighlights = [
  {
    title: "30,000 Trophies",
    description: "Get a head start on the leaderboard with 30,000 Trophies.",
    src: "https://cdn.navalabs.io/card-trophy-30k_hlt1xv.png",
  },
  {
    title: "5 Lucky Draw Tickets",
    description: "Enter the Lucky Draw and win up to $25 daily.",
    src: "https://cdn.navalabs.io/card-lucky-ticket_iplugh.png",
  },
];

export function WelcomePackDialog() {
  bonusHighlights.forEach((card) => preload(card.src, { as: "image" }));
  preload("/assets/cardpack_loop_hip1dh.webp", { as: "image" });

  const { ready, authenticated, user } = usePrivy();
  const isHomeUiReady = useAtomValue(homeUiReadyAtom);
  const setWelcomePackDialogOpen = useSetAtom(welcomePackDialogOpenAtom);
  const {
    mute: muteMusic,
    unmute: unmuteMusic,
    isMuted: isMusicMuted,
  } = useMusic();
  const userPublicKey = user?.wallet?.address ?? "";
  const {
    data: stats,
    isLoading: isStatsLoading,
    isError: isStatsError,
  } = useStats(userPublicKey);
  const { mutateAsync: claimWelcomePack, isPending: isClaimingWelcomePack } =
    useClaimWelcomePack();

  const [isOpen, setIsOpen] = useState(false);
  const [phase, setPhase] = useState<WelcomePhase>("intro");
  const [hasOpenedForEligibility, setHasOpenedForEligibility] = useState(false);
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isVideoBuffering, setIsVideoBuffering] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const preloadVideoRef = useRef<HTMLVideoElement | null>(null);
  const musicWasMutedBeforeVideoRef = useRef<boolean | null>(null);
  const storedMutePreferenceRef = useRef<boolean | null>(null);
  const hasActiveVideoMuteRef = useRef(false);
  const tweenFactorRef = useRef(0);
  const tweenNodesRef = useRef<HTMLElement[]>([]);

  const restoreMusicIfNeeded = useCallback(() => {
    if (!hasActiveVideoMuteRef.current) {
      return;
    }

    const storedPreference = storedMutePreferenceRef.current;
    const wasMutedBefore = musicWasMutedBeforeVideoRef.current;

    hasActiveVideoMuteRef.current = false;
    storedMutePreferenceRef.current = null;
    musicWasMutedBeforeVideoRef.current = null;

    const shouldRemainMuted =
      storedPreference !== null ? storedPreference : (wasMutedBefore ?? false);

    if (!shouldRemainMuted) {
      unmuteMusic();
    }
  }, [unmuteMusic]);

  useEffect(() => {
    return () => {
      restoreMusicIfNeeded();
    };
  }, [restoreMusicIfNeeded]);

  useEffect(() => {
    setWelcomePackDialogOpen(isOpen);
  }, [isOpen, setWelcomePackDialogOpen]);

  useEffect(() => {
    return () => {
      setWelcomePackDialogOpen(false);
    };
  }, [setWelcomePackDialogOpen]);

  useEffect(() => {
    if (!ready || !authenticated) {
      return;
    }

    // Wait for the home splash/intro to be dismissed before showing
    if (!isHomeUiReady) {
      return;
    }

    if (stats && stats.isWelcomePackEligible) {
      // if (true) {
      if (!hasOpenedForEligibility) {
        setIsOpen(true);
        setPhase("intro");
        setHasOpenedForEligibility(true);
      }
      return;
    }

    // if (stats && !stats.isWelcomePackEligible) {
    //   setIsOpen(false);
    //   setHasOpenedForEligibility(false);
    // }
  }, [
    authenticated,
    hasOpenedForEligibility,
    ready,
    stats,
    stats?.isWelcomePackEligible,
    isHomeUiReady,
  ]);

  const handleStartVideo = () => {
    if (!hasActiveVideoMuteRef.current) {
      musicWasMutedBeforeVideoRef.current = isMusicMuted;
      storedMutePreferenceRef.current = (() => {
        if (typeof window === "undefined") {
          return null;
        }
        try {
          const value = window.localStorage.getItem("cfl:music-muted");
          return value === null ? null : value === "true";
        } catch {
          return null;
        }
      })();

      if (!isMusicMuted) {
        hasActiveVideoMuteRef.current = true;
        muteMusic();
      }
    }

    setPhase("video");
    setIsVideoBuffering(true);
    requestAnimationFrame(() => {
      const element = videoRef.current;
      if (!element) return;

      const startPlayback = () => {
        setIsVideoBuffering(false);
        void element.play().catch((error) => {
          console.warn("Welcome pack video play() failed:", error);
        });
      };

      if (element.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        startPlayback();
      } else {
        element.addEventListener("canplay", startPlayback, { once: true });
      }
    });
  };

  const handleVideoEnded = () => {
    const element = videoRef.current;
    if (element) {
      element.pause();
    }
    setIsVideoBuffering(false);
    setPhase("cards");
    restoreMusicIfNeeded();
  };

  const handleClaim = async () => {
    try {
      await claimWelcomePack();
      handleStartVideo();
    } catch (error) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data
          ?.error ?? "Failed to claim welcome pack. Please try again.";
      toast.error(message, {
        id: "welcome-pack-claim-error",
      });
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      resetVideoState();
      restoreMusicIfNeeded();
    }
  };

  const resetVideoState = () => {
    const element = videoRef.current;
    if (element) {
      element.pause();
      element.currentTime = 0;
    }
    setIsVideoBuffering(false);
    setPhase("intro");
  };

  const setTweenNodes = useCallback((api: NonNullable<CarouselApi>) => {
    tweenNodesRef.current = api.slideNodes().map((slideNode) => {
      const target =
        slideNode.querySelector<HTMLElement>("[data-welcome-card]") ??
        (slideNode as HTMLElement);
      return target;
    });
  }, []);

  const setTweenFactor = useCallback((api: NonNullable<CarouselApi>) => {
    tweenFactorRef.current = TWEEN_FACTOR_BASE * api.scrollSnapList().length;
  }, []);

  const tweenScale = useCallback(
    (api: NonNullable<CarouselApi>, eventName?: EmblaEventType) => {
      const engine = api.internalEngine();
      const scrollProgress = api.scrollProgress();
      const slidesInView = api.slidesInView();
      const isScrollEvent = eventName === "scroll";

      api.scrollSnapList().forEach((scrollSnap, snapIndex) => {
        let diffToTarget = scrollSnap - scrollProgress;
        const slidesInSnap = engine.slideRegistry[snapIndex];

        slidesInSnap.forEach((slideIndex) => {
          if (isScrollEvent && !slidesInView.includes(slideIndex)) {
            return;
          }

          if (engine.options.loop) {
            engine.slideLooper.loopPoints.forEach((loopItem) => {
              const target = loopItem.target();

              if (slideIndex === loopItem.index && target !== 0) {
                const sign = Math.sign(target);

                if (sign === -1) {
                  diffToTarget = scrollSnap - (1 + scrollProgress);
                }

                if (sign === 1) {
                  diffToTarget = scrollSnap + (1 - scrollProgress);
                }
              }
            });
          }

          const tweenValue =
            1 - Math.abs(diffToTarget * tweenFactorRef.current);
          const scale = numberWithinRange(tweenValue, 0.8, 1).toString();
          const tweenNode = tweenNodesRef.current[slideIndex];

          if (tweenNode) {
            tweenNode.style.transform = `scale(${scale})`;
          }
        });
      });
    },
    [],
  );

  const tweenScaleOnScroll = useCallback(
    (api: NonNullable<CarouselApi>) => tweenScale(api, "scroll"),
    [tweenScale],
  );

  useEffect(() => {
    const api = carouselApi;
    if (!api || phase !== "cards") {
      return;
    }

    setTweenNodes(api);
    setTweenFactor(api);
    tweenScale(api);
    setActiveIndex(api.selectedScrollSnap());

    api
      .on("reInit", setTweenNodes)
      .on("reInit", setTweenFactor)
      .on("reInit", tweenScale)
      .on("scroll", tweenScaleOnScroll)
      .on("slideFocus", tweenScale)
      .on("select", () => setActiveIndex(api.selectedScrollSnap()));

    return () => {
      api.off("reInit", setTweenNodes);
      api.off("reInit", setTweenFactor);
      api.off("reInit", tweenScale);
      api.off("scroll", tweenScaleOnScroll);
      api.off("slideFocus", tweenScale);
    };
  }, [
    carouselApi,
    phase,
    setTweenNodes,
    setTweenFactor,
    tweenScale,
    tweenScaleOnScroll,
  ]);

  if (!ready || !authenticated || isStatsLoading || isStatsError) {
    return null;
  }

  if (!stats?.isWelcomePackEligible && !isOpen) {
    return null;
  }

  const shouldShowIntro = phase === "intro";
  const shouldShowVideo = phase === "video";
  const shouldShowCards = phase === "cards";

  // Duplicate slides so Embla has enough to create a proper loop with peek on both sides
  const carouselSlides = [...bonusHighlights, ...bonusHighlights];
  const activeHighlight =
    bonusHighlights[activeIndex % bonusHighlights.length] ?? bonusHighlights[0];

  // Determine if we should show the hidden preload video
  const shouldPreloadVideo =
    ready &&
    authenticated &&
    !isStatsLoading &&
    !isStatsError &&
    stats?.isWelcomePackEligible === true;

  return (
    <>
      {/* Hidden video element for preloading - renders when user is eligible */}
      {shouldPreloadVideo && (
        <video
          ref={preloadVideoRef}
          src={VIDEO_SRC}
          preload="auto"
          playsInline
          className="hidden"
          aria-hidden="true"
        />
      )}
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent
          className="bg-[#161616]! rounded-none! w-full h-full max-h-svh! max-w-svw! z-1000!"
          style={{
            borderImage: "none",
          }}
        >
          <DialogClose asChild>
            <button
              type="button"
              className="absolute right-4 top-4 z-50 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-white/20"
            >
              Close
            </button>
          </DialogClose>
          {shouldShowIntro && (
            <div className="flex flex-1 flex-col items-center justify-center gap-6 px-8 text-center sm:px-10">
              <img
                src="/assets/cardpack_loop_hip1dh.webp"
                className="w-1/3 rotate-12"
                style={{ imageRendering: "pixelated" }}
              />
              <DialogHeader className="space-y-3 text-center">
                <DialogTitle
                  className="text-3xl font-bold text-white text-center capitalize"
                  style={{
                    textShadow: generateTextShadow(1),
                  }}
                >
                  You've received Your Welcome Pack!
                </DialogTitle>
                <DialogDescription className="text-base text-white/80 text-center">
                  Kickstart your trading journey on CFL.
                </DialogDescription>
              </DialogHeader>

              <DefaultButton
                variant="green"
                onClick={handleClaim}
                disabled={isClaimingWelcomePack}
                className="h-10 uppercase"
              >
                {isClaimingWelcomePack ? (
                  <AnimatedWaiting prefix="Claiming" />
                ) : (
                  "Open your pack"
                )}
              </DefaultButton>
            </div>
          )}
          {shouldShowCards && (
            <div className="flex mx-auto max-w-lg flex-col gap-6 pt-8 pb-2">
              {/* <div className="text-center hidden"> */}
              {/*   <h2 className="text-2xl font-bold text-white uppercase"> */}
              {/*     Your Exclusive Perks */}
              {/*   </h2> */}
              {/*   <p className="mt-2 text-sm text-white/70"> */}
              {/*     Start exploring these exclusive benefits unlocked with your */}
              {/*     bonus pack. */}
              {/*   </p> */}
              {/* </div> */}

              <div className="mt-12 flex-1 min-h-0 flex flex-col justify-center items-center gap-12 sm:gap-8">
                <Carousel
                  className="flex max-h-fit w-full"
                  opts={{ align: "center", skipSnaps: true, loop: true }}
                  setApi={setCarouselApi}
                >
                  <CarouselContent className="mt-24 sm:mt-6 flex-1 flex items-center px-2 select-none">
                    {carouselSlides.map((highlight, index) => (
                      <CarouselItem
                        key={`${highlight.title}-${index}`}
                        style={{
                          flex: "0 0 50%",
                        }}
                      >
                        <img
                          src={highlight.src}
                          alt={highlight.title}
                          data-welcome-card
                          className="transition-transform duration-300 ease-out will-change-transform"
                        />
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                </Carousel>
                <div className="mt-6 flex flex-col items-center justify-center px-4 sm:px-9">
                  <h4 className="text-white text-3xl">
                    {activeHighlight.title}
                  </h4>
                  <p className="mt-2 text-sm sm:text-base text-center text-white/70 min-h-24 sm:min-h-16">
                    {activeHighlight.description}
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2 justify-center items-center px-2">
                <DialogClose asChild>
                  <DefaultButton className="w-full h-10 uppercase">
                    Jump In
                  </DefaultButton>
                </DialogClose>
                <button
                  type="button"
                  onClick={handleStartVideo}
                  className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Replay Video
                </button>
              </div>
            </div>
          )}
          {shouldShowVideo && (
            <div
              className="absolute inset-0 flex items-center justify-center bg-[#161616] z-[1001] cursor-pointer"
              onClick={handleVideoEnded}
            >
              <video
                ref={videoRef}
                className="h-full w-auto object-cover pointer-events-none"
                src={VIDEO_SRC}
                controls={false}
                playsInline
                preload="auto"
                onEnded={handleVideoEnded}
              />
              {isVideoBuffering && (
                <div className="absolute inset-0 flex items-center justify-center bg-[#161616] pointer-events-none">
                  <AnimatedWaiting prefix="Loading" />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
